import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Modal,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Fade,
  Backdrop,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as PlayArrowIcon,
  Close as CloseIcon,
  GetApp as ExportIcon,
  TableChart as TableChartIcon,
  Description as DocumentIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { structuredQueriesAPI } from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface Project {
  id: string;
  name: string;
  file_count: number;
  total_size: number;
}

interface ProjectFile {
  id: string;
  display_name: string;
  file_type: string;
  file_size: number;
  status: string;
  uploaded_at: string;
}

interface QueryColumn {
  id: string;
  name: string;
  query: string;
  order: number;
  type: string;
}

interface StructuredQuery {
  id: string;
  name: string;
  description?: string;
  original_query: string;
  query_type: string;
  status: string;
  created_at: string;
  last_run_at?: string;
  columns: QueryColumn[];
  files?: ProjectFile[];
  results?: { [key: string]: any };
  creator: {
    id: string;
    username: string;
    full_name: string;
  };
}

interface QueryTableProps {
  project: Project;
}

const QueryTable: React.FC<QueryTableProps> = ({ project }) => {
  const [structuredQueries, setStructuredQueries] = useState<StructuredQuery[]>([]);
  const [selectedQuery, setSelectedQuery] = useState<StructuredQuery | null>(null);
  const [queryTableData, setQueryTableData] = useState<ProjectFile[]>([]);
  const [queryResults, setQueryResults] = useState<Map<string, any>>(new Map());
  const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set());
  const [showCreateQueryModal, setShowCreateQueryModal] = useState(false);
  const [showAddColumnModal, setShowAddColumnModal] = useState(false);
  const [newQueryText, setNewQueryText] = useState('');
  const [isAddingColumn, setIsAddingColumn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form data for creating queries
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    original_query: '',
    query_type: 'analysis'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadStructuredQueries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await structuredQueriesAPI.getStructuredQueries(project.id);
      setStructuredQueries(response.queries || []);
      
      if (response.queries && response.queries.length > 0) {
        await loadQueryTable(response.queries[0].id);
      }
    } catch (error: any) {
      console.error('Error loading structured queries:', error);
      setError('Failed to load structured queries');
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    loadStructuredQueries();
  }, [project.id, loadStructuredQueries]);

  const loadQueryTable = async (queryId: string) => {
    try {
      const queryData = await structuredQueriesAPI.getStructuredQuery(queryId);
      setSelectedQuery(queryData);
      setQueryTableData(queryData.files || []);
      
      const resultsMap = new Map();
      Object.entries(queryData.results || {}).forEach(([key, result]: [string, any]) => {
        resultsMap.set(key, result);
      });
      setQueryResults(resultsMap);
    } catch (error: any) {
      console.error('Error loading query table:', error);
      setError('Failed to load query table');
    }
  };

  const handleCreateQuery = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.original_query.trim()) {
      setError('Please fill in required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await structuredQueriesAPI.createStructuredQuery(project.id, {
        ...formData,
        columns: [
          {
            name: 'Original Query',
            query: formData.original_query,
            type: 'analysis'
          }
        ]
      });
      
      setShowCreateQueryModal(false);
      setFormData({ name: '', description: '', original_query: '', query_type: 'analysis' });
      await loadStructuredQueries();
      
      // Backend now auto-runs queries when created, so we just need to reload data
      if (response.id) {
        // Wait a bit for the auto-run to start, then reload to show running status
        setTimeout(() => {
          loadQueryTable(response.id);
        }, 1000);
        
        // Poll for completion
        const pollForCompletion = async () => {
          try {
            const queryData = await structuredQueriesAPI.getStructuredQuery(response.id);
            if (queryData.status === 'completed' || queryData.status === 'failed') {
              await loadQueryTable(response.id);
            } else if (queryData.status === 'running') {
              setTimeout(pollForCompletion, 3000); // Poll every 3 seconds
            }
          } catch (error) {
            console.error('Error polling query status:', error);
          }
        };
        
        setTimeout(pollForCompletion, 2000);
      }
    } catch (error: any) {
      console.error('Error creating structured query:', error);
      setError('Failed to create structured query');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddColumn = async () => {
    if (!newQueryText.trim() || !selectedQuery) {
      setError('Please enter a query');
      return;
    }


    setIsAddingColumn(true);

    try {
      const response = await structuredQueriesAPI.addQueryColumn(selectedQuery.id, {
        name: newQueryText.trim(),
        query: newQueryText.trim(),
        type: 'analysis'
      });

      // Immediately add the new column to the UI with running status for all cells
      if (selectedQuery && response.id) {
        const newColumn: QueryColumn = {
          id: response.id,
          name: response.name,
          query: response.query,
          order: response.order,
          type: 'analysis'
        };
        
        // Update the selected query with the new column
        const updatedQuery = {
          ...selectedQuery,
          columns: [...selectedQuery.columns, newColumn]
        };
        setSelectedQuery(updatedQuery);
        
        // Set all cells for the new column to pending status immediately (will show as "Running...")
        const newResults = new Map(queryResults);
        for (const file of queryTableData) {
          const resultKey = `${file.id}-${response.id}`;
          newResults.set(resultKey, {
            status: 'pending',
            result_text: '',
            error_message: null
          });
        }
        setQueryResults(newResults);
      }

      setShowAddColumnModal(false);
      setNewQueryText('');

      // Poll for completion of the new column cells
      if (response.id && queryTableData.length > 0) {
        const pollForColumnCompletion = async () => {
          try {
            const queryData = await structuredQueriesAPI.getStructuredQuery(selectedQuery.id);
            
            // Update results for the new column
            const updatedResults = new Map(queryResults);
            let allCompleted = true;
            
            for (const file of queryTableData) {
              const resultKey = `${file.id}-${response.id}`;
              const result = queryData.results?.[resultKey];
              
              if (result) {
                updatedResults.set(resultKey, result);
                if (result.status === 'pending') {
                  allCompleted = false;
                }
              } else {
                allCompleted = false;
              }
            }
            
            setQueryResults(updatedResults);
            
            if (!allCompleted) {
              setTimeout(pollForColumnCompletion, 2000); // Poll every 2 seconds
            }
          } catch (error) {
            console.error('Error polling column completion:', error);
          }
        };
        
        // Start polling after a short delay
        setTimeout(pollForColumnCompletion, 1000);
      }
    } catch (error: any) {
      console.error('Error adding query column:', error);
      setError('Failed to add query column');
    } finally {
      setIsAddingColumn(false);
    }
  };

  const runQueryForFile = async (fileIndex: number, columnIndex: number) => {
    if (!selectedQuery) return;
    
    const file = queryTableData[fileIndex];
    const column = selectedQuery.columns[columnIndex];
    const resultKey = `${file.id}-${column.id}`;

    if (!file || !column) return;

    setQueryResults(prev => new Map(prev.set(resultKey, {
      status: 'pending',
      result_text: '',
      error_message: null
    })));

    try {
      const response = await structuredQueriesAPI.runQueryCell(selectedQuery.id, {
        column_id: column.id,
        document_id: file.id,
        simple_mode: true
      });
      setQueryResults(prev => new Map(prev.set(resultKey, response)));
    } catch (error: any) {
      console.error('Error running query for file:', error);
      setQueryResults(prev => new Map(prev.set(resultKey, {
        status: 'failed',
        result_text: '',
        error_message: error.response?.data?.detail || error.message
      })));
    }
  };

  const runQueryForAllFiles = async (columnIndex: number) => {
    if (!selectedQuery) return;
    
    const column = selectedQuery.columns[columnIndex];
    if (!column) return;

    try {
      await structuredQueriesAPI.runStructuredQuery(selectedQuery.id);
      
      setTimeout(() => {
        loadQueryTable(selectedQuery.id);
      }, 2000);
    } catch (error: any) {
      console.error('Error running query for all files:', error);
      setError('Failed to run query for all files');
    }
  };

  const toggleCellExpansion = (resultKey: string) => {
    setExpandedCells(prev => {
      const newSet = new Set(prev);
      if (newSet.has(resultKey)) {
        newSet.delete(resultKey);
      } else {
        newSet.add(resultKey);
      }
      return newSet;
    });
  };

  const handleExportToCSV = () => {
    if (!selectedQuery || queryTableData.length === 0) return;

    // Prepare CSV headers
    const headers = ['Document Name', ...selectedQuery.columns.map(col => col.name)];
    
    // Prepare CSV rows
    const rows = queryTableData.map(file => {
      const row = [file.display_name];
      
      selectedQuery.columns.forEach(column => {
        const resultKey = `${file.id}-${column.id}`;
        const result = queryResults.get(resultKey);
        
        let cellValue = '';
        if (result) {
          if (result.status === 'completed') {
            // Clean the text for CSV: remove newlines and escape quotes
            cellValue = (result.result_text || '')
              .replace(/\n/g, ' ')
              .replace(/"/g, '""');
          } else if (result.status === 'pending') {
            cellValue = 'Running...';
          } else if (result.status === 'failed') {
            cellValue = `Error: ${result.error_message || 'Query failed'}`;
          }
        } else {
          cellValue = 'Not run';
        }
        
        row.push(cellValue);
      });
      
      return row;
    });

    // Convert to CSV format
    const csvContent = [
      headers.map(h => `"${h}"`).join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${selectedQuery.name.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFileIcon = (fileType: string) => {
    return <DocumentIcon sx={{ color: 'text.secondary' }} />;
  };

  const renderQueryCell = (fileIndex: number, columnIndex: number) => {
    if (!selectedQuery) return null;
    
    const file = queryTableData[fileIndex];
    const column = selectedQuery.columns[columnIndex];
    const resultKey = `${file.id}-${column.id}`;
    const result = queryResults.get(resultKey);

    if (!result) {
      return (
        <Box
          onClick={() => runQueryForFile(fileIndex, columnIndex)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 80,
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover',
            }
          }}
        >
          <PlayArrowIcon sx={{ color: 'primary.main', fontSize: 32 }} />
          <Typography variant="caption" color="text.secondary">
            Click to run
          </Typography>
        </Box>
      );
    }

    if (result.status === 'pending') {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 80,
            backgroundColor: 'action.hover',
            borderRadius: 1,
          }}
        >
          <CircularProgress size={24} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Running...
          </Typography>
        </Box>
      );
    }

    if (result.status === 'failed') {
      return (
        <Box
          sx={{
            p: 1,
            backgroundColor: 'error.light',
            borderRadius: 1,
            minHeight: 80,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <ErrorIcon sx={{ color: 'error.main', fontSize: 16, mr: 0.5 }} />
            <Typography variant="caption" color="error.main">
              Query failed
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {result.error_message}
          </Typography>
        </Box>
      );
    }

    const isExpanded = expandedCells.has(resultKey);
    const hasLongText = result.result_text && result.result_text.length > 100;
    const displayText = isExpanded || !hasLongText 
      ? result.result_text || '' 
      : result.result_text.substring(0, 97) + '...';

    return (
      <Box
        sx={{
          p: 1,
          backgroundColor: 'success.light',
          borderRadius: 1,
          minHeight: 80,
          transition: 'all 0.2s ease',
        }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            mb: 1, 
            lineHeight: 1.3,
            whiteSpace: isExpanded ? 'pre-wrap' : 'normal',
            wordBreak: 'break-word'
          }}
        >
          {displayText}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Chip
            icon={<CheckCircleIcon />}
            label="Completed"
            size="small"
            color="success"
            variant="outlined"
          />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {hasLongText && (
              <Tooltip title={isExpanded ? "Collapse" : "Expand"}>
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCellExpansion(resultKey);
                  }}
                >
                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <LoadingSpinner message="Loading query tables..." />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        action={
          <Button 
            color="inherit" 
            size="small" 
            onClick={() => {
              setError(null);
              loadStructuredQueries();
            }}
          >
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (structuredQueries.length === 0) {
    return (
      <Paper sx={{ p: 6, textAlign: 'center' }}>
        <TableChartIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          No Query Tables Yet
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
          Create a structured query table to analyze multiple documents with column-based queries.
          Perfect for contract reviews, due diligence, and comparative analysis.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowCreateQueryModal(true)}
          size="large"
        >
          Create Query Table
        </Button>

        {/* Create Query Modal */}
        <Modal
          open={showCreateQueryModal}
          onClose={() => setShowCreateQueryModal(false)}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{ timeout: 500 }}
        >
          <Fade in={showCreateQueryModal}>
            <Paper
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '90%', sm: 600 },
                maxHeight: '90vh',
                overflow: 'auto',
                p: 4,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">
                  <TableChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Create Query Table
                </Typography>
                <IconButton onClick={() => setShowCreateQueryModal(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>

              <Box component="form" onSubmit={handleCreateQuery}>
                <TextField
                  fullWidth
                  label="Query Name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Contract Review Analysis"
                  required
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Original Query"
                  value={formData.original_query}
                  onChange={(e) => setFormData(prev => ({ ...prev, original_query: e.target.value }))}
                  placeholder="e.g., Identify the parties, change of control provisions, termination, and red flags generally"
                  multiline
                  rows={3}
                  required
                  sx={{ mb: 2 }}
                  helperText="This will be the main query that gets broken down into column sub-queries"
                />

                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description of what this query table will analyze"
                  multiline
                  rows={2}
                  sx={{ mb: 3 }}
                />

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setShowCreateQueryModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || !formData.name.trim() || !formData.original_query.trim()}
                    startIcon={isSubmitting ? <CircularProgress size={16} /> : <AddIcon />}
                  >
                    {isSubmitting ? 'Creating...' : 'Create Query Table'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Fade>
        </Modal>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Query Table Selector */}
      {structuredQueries.length > 1 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Select Query Table</InputLabel>
            <Select
              value={selectedQuery?.id || ''}
              onChange={(e) => {
                const queryId = e.target.value;
                if (queryId) {
                  loadQueryTable(queryId);
                }
              }}
              label="Select Query Table"
            >
              {structuredQueries.map((query) => (
                <MenuItem key={query.id} value={query.id}>
                  {query.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}

      {/* Header */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h5">
              {selectedQuery?.name || 'Query Table'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <Chip label={`${queryTableData.length} documents`} size="small" />
              <Chip label={`${selectedQuery?.columns?.length || 0} columns`} size="small" />
              {selectedQuery?.last_run_at && (
                <Chip 
                  label={`Last run ${new Date(selectedQuery.last_run_at).toLocaleDateString()}`} 
                  size="small" 
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateQueryModal(true)}
            >
              New Query Table
            </Button>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={handleExportToCSV}
              disabled={!selectedQuery || queryTableData.length === 0}
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Query Description */}
        {selectedQuery && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Original query:</strong> {selectedQuery.original_query}
            </Typography>
          </Alert>
        )}
      </Paper>

      {/* Table Controls */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddColumnModal(true)}
        >
          Add Column
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 250, fontWeight: 'bold' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DocumentIcon />
                  Document Name
                </Box>
              </TableCell>
              {selectedQuery?.columns?.map((column, index) => (
                <TableCell key={column.id} sx={{ minWidth: 200, fontWeight: 'bold' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2">
                        {column.name.length > 30 ? column.name.substring(0, 27) + '...' : column.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {column.type}
                      </Typography>
                    </Box>
                    <Tooltip title="Run for all files">
                      <IconButton
                        size="small"
                        onClick={() => runQueryForAllFiles(index)}
                      >
                        <PlayArrowIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {queryTableData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={selectedQuery?.columns?.length ? selectedQuery.columns.length + 1 : 1}>
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <DocumentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No completed documents in this project
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              queryTableData.map((file, fileIndex) => (
                <TableRow key={file.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {getFileIcon(file.file_type)}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {file.display_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(file.file_size / (1024 * 1024)).toFixed(1)} MB
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  {selectedQuery?.columns?.map((column, columnIndex) => (
                    <TableCell key={`${fileIndex}-${columnIndex}`}>
                      {renderQueryCell(fileIndex, columnIndex)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Column Modal */}
      <Modal
        open={showAddColumnModal}
        onClose={() => setShowAddColumnModal(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={showAddColumnModal}>
          <Paper
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: 500 },
              p: 4,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                <AddIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Add Query Column
              </Typography>
              <IconButton onClick={() => setShowAddColumnModal(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            <TextField
              fullWidth
              label="Column Query"
              value={newQueryText}
              onChange={(e) => setNewQueryText(e.target.value)}
              placeholder="Enter your query (e.g., 'What are the key risks mentioned?')"
              multiline
              rows={3}
              sx={{ mb: 2 }}
              helperText="This query will be run against all documents in the project"
            />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => setShowAddColumnModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleAddColumn}
                disabled={isAddingColumn || !newQueryText.trim()}
                startIcon={isAddingColumn ? <CircularProgress size={16} /> : <AddIcon />}
              >
                {isAddingColumn ? 'Adding...' : 'Add Column'}
              </Button>
            </Box>
          </Paper>
        </Fade>
      </Modal>

      {/* Create Query Modal */}
      <Modal
        open={showCreateQueryModal}
        onClose={() => setShowCreateQueryModal(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={showCreateQueryModal}>
          <Paper
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: 600 },
              maxHeight: '90vh',
              overflow: 'auto',
              p: 4,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5">
                <TableChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Create Query Table
              </Typography>
              <IconButton onClick={() => setShowCreateQueryModal(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box component="form" onSubmit={handleCreateQuery}>
              <TextField
                fullWidth
                label="Query Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Contract Review Analysis"
                required
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Original Query"
                value={formData.original_query}
                onChange={(e) => setFormData(prev => ({ ...prev, original_query: e.target.value }))}
                placeholder="e.g., Identify the parties, change of control provisions, termination, and red flags generally"
                multiline
                rows={3}
                required
                sx={{ mb: 2 }}
                helperText="This will be the main query that gets broken down into column sub-queries"
              />

              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description of what this query table will analyze"
                multiline
                rows={2}
                sx={{ mb: 3 }}
              />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => setShowCreateQueryModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || !formData.name.trim() || !formData.original_query.trim()}
                  startIcon={isSubmitting ? <CircularProgress size={16} /> : <AddIcon />}
                >
                  {isSubmitting ? 'Creating...' : 'Create Query Table'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Modal>
    </Box>
  );
};

export default QueryTable;
